<template>
  <div class="card visuwell-create-user">
    <div class="card-content">
      <question-title :question="question" class="mb2"></question-title>

      <div class="buttons">
        <button
          :class="[
            'button',
            'is-fullwidth',
            'mb1',
            { 'is-primary': formAction == 'find' }
          ]"
          @click="setFormAction('find')"
        >
          Lookup an Existing Patient
        </button>
        <button
          :class="[
            'button',
            'is-fullwidth',
            'mb1',
            { 'is-primary': formAction == 'create' }
          ]"
          @click="setFormAction('create')"
        >
          Create a New Patient
        </button>
      </div>

      <div v-if="showForm">
        <div class="select mt1 is-fullwidth">
          <label class="mb1">Location</label>
          <select v-model="organizationLocationId">
            <option value="" disabled>Choose organization location</option>
            <option
              v-for="field in question.fields"
              v-bind:key="field.organizationLocationId"
              :value="field.organizationLocationId"
            >
              {{ field.choice }}
            </option>
          </select>
        </div>

        <div class="control mt1">
          <label class="mb1">MRN</label>
          <input class="input" v-model="emrPatientIdentifier" type="text" />
        </div>

        <div class="control mt1">
          <label class="mb1">First Name</label>
          <input class="input" v-model="firstName" type="text" />
        </div>

        <div class="control mt1">
          <label class="mb1">Last Name</label>
          <input class="input" v-model="lastName" type="text" />
        </div>

        <div class="date-picker control mt1 mb1">
          <label class="mb1">Date of Birth</label>
          <div v-if="isIOS()">
            <input v-model="dateOfBirth" type="date" class="datetime-input" />
          </div>
          <datetime
            v-model="dateOfBirth"
            v-if="!isIOS()"
            :auto="true"
            :autoDate="false"
            zone="UTC"
            valueZone="UTC"
            :format="config.format"
          ></datetime>
          <span class="icon is-right"><i class="icon icon-calendar"></i></span>
        </div>
        <h4 v-if="actionMessage" class="mb1 mt1 has-text-centered">
          {{ actionMessage }}
        </h4>
        <next-button
          ref="nextButton"
          :id="question.id"
          :choice="choice"
          @onBeforeNextQuestion="performFormAction"
        ></next-button>
      </div>
    </div>
  </div>
</template>

<script>
import baseQuestion from "./baseQuestion";
import { isIOS } from "mobile-device-detect";
import { DateTime } from "luxon";
import { isEmpty } from "lodash";
import VisuwellService from "../../services/VisuwellService";

export default {
  name: "visuwell_user",

  mixins: [baseQuestion],

  mounted() {
    if (this.choice && this.choice.action) {
      this.showForm = true;
      this.formAction = this.choice.action;
      this.organizationLocationId = this.choice.organizationLocationId;
      this.dateOfBirth = this.choice.dateOfBirth;
      this.lastName = this.choice.lastName;
      this.emrPatientIdentifier = this.choice.emrPatientIdentifier;
    }
  },

  data() {
    return {
      dateOfBirth: null,
      emrPatientIdentifier: null,
      organizationLocationId: "",
      firstName: null,
      lastName: null,
      config: {
        format: DateTime.DATE_FULL
      },
      goToNextQuestion: false,
      showForm: false,
      formAction: null,
      actionMessage: null
    };
  },

  computed: {
    interviewId() {
      return this.$store.state.interview.id;
    },
    patientQueueId() {
      return this.$route.params.id;
    },
    organizationId() {
      return this.question.meta.organizationId;
    }
  },

  watch: {
    dateOfBirth() {
      this.validate();
    },
    emrPatientIdentifier() {
      this.validate();
    },
    organizationLocationId() {
      this.validate();
    }
  },

  methods: {
    setFormAction(formAction) {
      this.formAction = formAction;
      this.showForm = true;
    },
    cancelNextQuestion() {
      this.$store.dispatch("interview/setNext", false);
    },
    retryNextQuestion() {
      this.$store.dispatch("interview/setNext", true);
      this.$nextTick(() => {
        let btn = this.$refs.nextButton.$el;
        if (btn) {
          btn.click();
        }
      });
    },
    validate() {
      if (this.question.required === false) return;

      let organizationLocationIdSet = !isEmpty(this.organizationLocationId);
      let firstNameSet = !isEmpty(this.firstName);
      let lastNameSet = !isEmpty(this.lastName);
      let emrPatientIdentifierSet = !isEmpty(this.emrPatientIdentifier);
      let parsedDateOfBirth = DateTime.fromISO(this.dateOfBirth);
      if (
        organizationLocationIdSet &&
        firstNameSet &&
        lastNameSet &&
        emrPatientIdentifierSet &&
        parsedDateOfBirth.isValid
      ) {
        this.$store.dispatch("interview/setNext", true);
      } else {
        this.$store.dispatch("interview/setNext", false);
      }
    },

    isIOS() {
      return isIOS;
    },
    performFormAction(field) {
      this.loading = true;
      this.actionMessage = null;
      if (this.goToNextQuestion) {
      } else {
        this.cancelNextQuestion();
        let payload = {
          emrPatientIdentifier: this.emrPatientIdentifier,
          organizationLocationId: this.organizationLocationId,
          dateOfBirth: this.dateOfBirth,
          firstName: this.firstName,
          lastName: this.lastName,
          interviewId: this.interviewId,
          patientQueueId: this.patientQueueId,
          organizationId: this.organizationId
        };

        const handleError = error => {
          this.loading = false;
          console.error("error=", error);
          this.$store.dispatch("interview/setNext", true);
          this.actionMessage = error;
        };

        const handleSuccess = (payload, errorMessage) => {
          return data => {
            this.loading = false;
            this.$store.dispatch("interview/setNext", true);
            if (data.id) {
              this.choice = {
                action: this.formAction,
                userId: data.id,
                ...payload
              };
              this.goToNextQuestion = true;
              this.retryNextQuestion();
            } else {
              handleError(errorMessage);
            }
          };
        };

        switch (this.formAction) {
          case "find":
            VisuwellService.findUser(payload)
              .then(
                handleSuccess(payload, "Sorry we could not find that patient.")
              )
              .catch(handleError);
            break;
          case "create":
            VisuwellService.createUser(payload)
              .then(
                handleSuccess(
                  payload,
                  "Sorry we could not create that patient."
                )
              )
              .catch(handleError);
            break;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.buttons {
  display: flex;
  justify-content: space-between;
  .button {
    width: 49%;
  }
}
</style>
